import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { AsyncPipe } from "@angular/common";
import { ChangeDetectorRef, Component, OnDestroy, OnInit, signal } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatNavList } from "@angular/material/list";
import { MatSidenavModule } from "@angular/material/sidenav";
import { Title } from "@angular/platform-browser";
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from "@angular/router";
import { MessagesUnreadService } from "@pages/messages/messages-unread.service";
import { AppStateService } from "@services/index";
import { RequestService } from "@services/request/request.service";
import { clearLocalStorageUtil } from "@utils/clear-local-storage.util";
import { Observable, Subscription } from "rxjs";
import { filter, map, shareReplay } from "rxjs/operators";
import { ImageUrlDirective } from "../../directives/image-url.directive";
import { NavDotsService } from "./nav-dots.service";

interface RouteInfo {
  path: string;
  title: string;
  icon: string;
  class: string;
  /** A custom image icon, used instead of the mat-icon ligature when set. */
  image?: string;
  /** A red dot with a count beside the title (NavDotsService). */
  badge?: "vendors" | "users" | "messages" | "orders" | "pending" | "products";
}
export const ROUTES: RouteInfo[] = [
  {
    path: "/dashboard",
    title: "Dashboard",
    icon: "dashboard",
    class: "",
  },

  {
    path: "/vendors",
    title: "Vendor List",
    icon: "people",
    class: "",
    badge: "vendors",
  },
  {
    path: "/product-sync",
    title: "Product Sync",
    icon: "sync",
    class: "",
  },
  {
    path: "/fishyops-ai",
    title: "FishyOps AI",
    icon: "smart_toy",
    image: "assets/images/fishyops-kraken.png",
    class: "",
  },
  {
    path: "/messages",
    title: "Messages",
    icon: "forum",
    class: "",
    badge: "messages",
  },
  {
    path: "/user_list",
    title: "User List",
    icon: "people",
    class: "",
    badge: "users",
  },
  {
    path: "/access-approval",
    title: "Access Approval",
    icon: "how_to_reg",
    class: "",
  },
  {
    path: "/categories",
    title: "Category List",
    icon: "list_alt",
    class: "",
  },

  {
    path: "/products",
    title: "Product List",
    icon: "inventory",
    class: "",
    badge: "products",
  },
  {
    path: "/orders",
    title: "Orders List",
    icon: "shopping_cart",
    class: "",
    badge: "orders",
  },
  {
    path: "/pending-order-list",
    title: "Pending Orders List",
    icon: "hourglass_empty",
    class: "",
    badge: "pending",
  },
  {
    path: "/recommendations",
    title: "Recommendation List",
    icon: "thumb_up",
    class: "",
  },
  {
    path: "/consumer-outreach",
    title: "Consumer Outreach",
    icon: "campaign",
    class: "",
  },
  {
    path: "/admin/merchantOrders",
    title: "Merchant Order List",
    icon: "local_shipping",
    class: "",
  },
  {
    path: "/banners",
    title: "Banners",
    icon: "campaign",
    class: "",
  },
  {
    path: "/media-uploader",
    title: "CDN Image Uploader",
    icon: "cloud_upload",
    class: "",
  },
  {
    path: "/campaign",
    title: "Campaigns",
    icon: "campaign",
    class: "",
  },
  {
    path: "/offer",
    title: "Offer List",
    icon: "local_offer",
    class: "",
  },
  {
    path: "/auction",
    title: "Auction",
    icon: "gavel",
    class: "",
  },
  {
    path: "/countries",
    title: "Country List",
    icon: "language",
    class: "",
  },
  {
    path: "/seo",
    title: "SEO Management",
    icon: "search",
    class: "",
  },
  {
    path: "/blogs",
    title: "Blogs Manage",
    icon: "article",
    class: "",
  },
  {
    path: "/delivery-discount",
    title: "Delivery Discount",
    icon: "local_shipping",
    class: "",
  },
  {
    path: "/transhipment",
    title: "Transhipment Default",
    icon: "flight_land",
    class: "",
  },
  {
    path: "/cross-border-restriction",
    title: "Cross Border Restriction",
    icon: "block",
    class: "",
  },
  {
    path: "/transhippers",
    title: "Transhipper List",
    icon: "local_shipping",
    class: "",
  },
  {
    path: "/tags",
    title: "Tag Management",
    icon: "label",
    class: "",
  },
  {
    path: "/community",
    title: "Giveaway Posts",
    icon: "forum",
    class: "",
  },
  {
    path: "/feed-posts",
    title: "Feed Posts",
    icon: "smart_display",
    class: "",
  },
  {
    path: "/change-log",
    title: "Change Log",
    icon: "history",
    class: "",
  },
  {
    path: "/bulletin",
    title: "Fishyhub Bulletin",
    icon: "campaign",
    class: "",
  },
];

@Component({
  selector: "fh-main-nav",
  templateUrl: "./main-nav.component.html",
  styleUrls: ["./main-nav.component.scss"],
  standalone: true,
  imports: [
    MatSidenavModule,
    MatIconModule,
    MatButtonModule,
    AsyncPipe,
    RouterLink,
    RouterLinkActive,
    MatNavList,
    ImageUrlDirective,
  ],
})
export class MainNavComponent implements OnDestroy, OnInit {
  access: string | null = null;
  token = "";
  username = "";
  profilePic = "";
  menuItems: any[];
  pageTitle = signal<string>("Fishyhub Admin Portal");
  isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset).pipe(
    map((result) => result.matches),
    shareReplay()
  );
  tokenSubs!: Subscription;
  userSubs!: Subscription;
  routerSubs!: Subscription;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    private auth: RequestService,
    private appStateService: AppStateService,
    private cdr: ChangeDetectorRef,
    private titleService: Title,
    readonly messagesUnread: MessagesUnreadService,
    readonly navDots: NavDotsService
  ) {
    this.menuItems = ROUTES;
  }

  ngOnInit() {
    this.tokenSubs = this.appStateService.userToken$.subscribe((val) => {
      this.token = val;
      this.access = this.appStateService.currentUser$.value?.access ?? null;
      if (val) {
        this.messagesUnread.start();
        this.navDots.start();
      } else {
        this.messagesUnread.stop();
        this.navDots.stop();
      }
      // this.checkRoutes();
      this.cdr.detectChanges();
    });

    this.userSubs = this.appStateService.currentUser$.subscribe((user) => {
      this.username = user?.name ?? "";
      this.profilePic = user?.profile_pic ?? "";
      this.cdr.detectChanges();
    });

    // Subscribe to router events to update page title
    this.routerSubs = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.updatePageTitle(event.urlAfterRedirects);
        this.markDotSeen(event.urlAfterRedirects);
      });

    // Update title on initial load
    this.updatePageTitle(this.router.url);
    this.markDotSeen(this.router.url);
  }

  /** Opening a page with a "new since" count clears it. */
  private markDotSeen(url: string): void {
    if (url.startsWith("/vendors")) this.navDots.markSeen("vendors");
    else if (url.startsWith("/user_list")) this.navDots.markSeen("users");
    else if (url.startsWith("/messages")) this.navDots.markSeen("messages");
    else if (url.startsWith("/pending-order-list")) this.navDots.markSeen("pending");
    else if (url.startsWith("/products")) this.navDots.markSeen("products");
  }

  /** The number in a menu item's red dot (0: no dot). */
  dotCount(badge: RouteInfo["badge"]): number {
    switch (badge) {
      case "vendors":
        return this.navDots.vendors();
      case "users":
        return this.navDots.users();
      case "orders":
        return this.navDots.orders();
      case "pending":
        return this.navDots.pending();
      case "products":
        return this.navDots.products();
      case "messages":
        // New messages plus flagged conversations; MessagesUnreadService has the
        // flagged count the moment the Messages page reviews one.
        return this.navDots.newMessages() + this.messagesUnread.count();
      default:
        return 0;
    }
  }

  private updatePageTitle(url: string): void {
    const routeTitle = this.getPageTitleFromUrl(url);
    const accessType = this.access
      ? `${this.access.charAt(0).toUpperCase() + this.access.slice(1)}`
      : "Admin";

    const fullTitle = routeTitle
      ? `${routeTitle} - Fishyhub ${accessType} Portal`
      : `Fishyhub ${accessType} Portal`;

    this.pageTitle.set(routeTitle || "Fishyhub Admin Portal");
    this.titleService.setTitle(fullTitle);
    this.cdr.detectChanges();
  }

  private getPageTitleFromUrl(url: string): string | null {
    // Remove query params and hash
    const cleanUrl = url.split("?")[0].split("#")[0];

    // Check exact matches first
    const exactMatch = ROUTES.find((route) => route.path === cleanUrl);
    if (exactMatch) {
      return exactMatch.title;
    }

    // Check for dynamic routes (e.g., /products/:merchantId/:categoryId)
    for (const route of ROUTES) {
      const routePath = route.path;
      // Convert route path pattern to regex
      const pattern = routePath.replace(/:[^/]+/g, "[^/]+");
      const regex = new RegExp(`^${pattern}$`);
      if (regex.test(cleanUrl)) {
        return route.title;
      }
    }

    // Check child routes
    if (cleanUrl.startsWith("/campaign/")) {
      if (cleanUrl.includes("/create") || cleanUrl.match(/\/campaign\/\d+/)) {
        return "Campaign Details";
      }
      return "Campaigns";
    }

    if (cleanUrl.startsWith("/banners/")) {
      if (cleanUrl.includes("/add-banner")) {
        return "Add Banner";
      }
      if (cleanUrl.includes("/edit-banner")) {
        return "Edit Banner";
      }
      return "Banners";
    }

    if (cleanUrl.startsWith("/offer/")) {
      return "Offer Details";
    }

    if (cleanUrl.startsWith("/auction/")) {
      return "Auction Details";
    }

    if (cleanUrl.startsWith("/seo/")) {
      if (cleanUrl.includes("/create")) {
        return "Create SEO Entry";
      }
      return "SEO Management";
    }

    if (cleanUrl.startsWith("/blogs/")) {
      if (cleanUrl.includes("/create")) {
        return "Create Blog";
      }
      if (cleanUrl.includes("/edit/")) {
        return "Edit Blog";
      }
      return "Blogs Manage";
    }

    if (cleanUrl.startsWith("/countries/")) {
      return "Country Details";
    }

    if (cleanUrl.startsWith("/product_input/")) {
      return "Product Details";
    }

    if (cleanUrl.startsWith("/category_input/")) {
      return "Category Details";
    }

    if (cleanUrl.startsWith("/vendor_input/")) {
      return "Vendor Details";
    }

    if (cleanUrl.startsWith("/transhipper_input/")) {
      return "Transhipper Details";
    }

    if (cleanUrl.startsWith("/order_details/")) {
      return "Order Details";
    }

    if (cleanUrl.startsWith("/admin/user-details/")) {
      return "User Details";
    }

    return null;
  }

  ngOnDestroy() {
    if (this.tokenSubs) {
      this.tokenSubs.unsubscribe();
    }
    if (this.userSubs) {
      this.userSubs.unsubscribe();
    }
    if (this.routerSubs) {
      this.routerSubs.unsubscribe();
    }
    this.messagesUnread.stop();
    this.navDots.stop();
  }

  logout() {
    const finish = () => {
      clearLocalStorageUtil();
      this.appStateService.userToken$.next("");
      this.appStateService.currentUser$.next(null);
      // Hard reload rather than router.navigate — logout clears a cookie the
      // frontend can't otherwise "see", so a full re-bootstrap is needed to
      // reliably resync app state, consistent with the interceptor's
      // expired-session handling.
      window.location.href = "/login";
    };
    this.auth.delete("/api/v1/users/logout").subscribe({
      next: finish,
      error: finish,
    });
  }
}
